import { countries, getCountryDataList } from "countries-list";
import type { TCountryCode } from "countries-list";
import {
  getExampleNumber,
  isPossiblePhoneNumber,
  isSupportedCountry,
  parsePhoneNumberFromString,
} from "libphonenumber-js/min";
import mobileExamples from "libphonenumber-js/mobile/examples";

export interface DialCodeOption {
  code: TCountryCode;
  name: string;
  dialCode: string;
}

let phoneExampleDialCodeOptions: DialCodeOption[] | undefined;

const PRIMARY_DIAL_CODE_COUNTRY: Record<string, TCountryCode> = {
  "+1": "US",
  "+7": "RU",
  "+44": "GB",
  "+61": "AU",
  "+212": "MA",
  "+262": "RE",
  "+590": "GP",
  "+599": "CW",
};

function getCountryName(code: TCountryCode, locale?: string): string {
  const country = countries[code];
  if (!locale) return country.name;

  try {
    return new Intl.DisplayNames(locale, { type: "region" }).of(code) ?? country.name;
  } catch {
    return country.name;
  }
}

function getCollator(locale?: string): Intl.Collator {
  try {
    return new Intl.Collator(locale);
  } catch {
    return new Intl.Collator("en");
  }
}

export function getDialCodeOptions(locale?: string): DialCodeOption[] {
  const options = getCountryDataList().flatMap((country) => {
    if (country.phone.length === 0) return [];
    return [
      {
        code: country.iso2,
        name: getCountryName(country.iso2, locale),
        dialCode: `+${country.phone[0]}`,
      },
    ];
  });

  const collator = getCollator(locale);
  return options.sort((left, right) => collator.compare(left.name, right.name));
}

export function parsePhoneValue(
  value: string,
  options: DialCodeOption[],
  preferred: TCountryCode,
): { country: TCountryCode; nationalNumber: string } {
  const digits = value.replace(/\D/g, "");
  if (!value.startsWith("+")) {
    return { country: preferred, nationalNumber: digits };
  }

  const matches = options.filter((option) => value.startsWith(option.dialCode));
  if (matches.length === 0) {
    return { country: preferred, nationalNumber: digits };
  }

  const longestLength = Math.max(...matches.map((option) => option.dialCode.length));
  const longestMatches = matches.filter((option) => option.dialCode.length === longestLength);
  const preferredMatch = longestMatches.find((option) => option.code === preferred);
  const primaryCode = longestMatches.find((option) => PRIMARY_DIAL_CODE_COUNTRY[option.dialCode] === option.code);
  const selected =
    preferredMatch ?? primaryCode ?? [...longestMatches].sort((left, right) => left.code.localeCompare(right.code))[0];

  return {
    country: selected.code,
    nationalNumber: digits.slice(selected.dialCode.slice(1).length),
  };
}

export function countryForDialCode(
  dialCode: string,
  options: DialCodeOption[],
  preferredCountry?: TCountryCode,
): TCountryCode | undefined {
  const matches = options.filter((option) => option.dialCode === dialCode);
  const preferred = matches.find((option) => option.code === preferredCountry);
  const primary = matches.find((option) => PRIMARY_DIAL_CODE_COUNTRY[dialCode] === option.code);
  return (
    preferred?.code ?? primary?.code ?? [...matches].sort((left, right) => left.code.localeCompare(right.code))[0]?.code
  );
}

export function composePhoneValue(country: TCountryCode, dialCode: string, nationalNumber: string): string {
  const digits = nationalNumber.replace(/\D/g, "");
  if (!digits) return "";

  if (isSupportedCountry(country)) {
    const parsed = parsePhoneNumberFromString(digits, country);
    if (parsed && parsed.countryCallingCode === dialCode.slice(1)) {
      return parsed.number;
    }
  }

  return `${dialCode}${digits}`;
}

export const PHONE_E164_PATTERN = /^\+[1-9]\d{6,14}$/;

export function isValidPhoneValue(value: string): boolean {
  return PHONE_E164_PATTERN.test(value) && isPossiblePhoneNumber(value);
}

export function getPhoneExample(value: string, preferredCountry?: string): string | undefined {
  if (!phoneExampleDialCodeOptions) {
    phoneExampleDialCodeOptions = getDialCodeOptions();
  }
  const options = phoneExampleDialCodeOptions;
  const preferred = options.find((option) => option.code === preferredCountry?.toUpperCase())?.code ?? "GB";
  const { country } = parsePhoneValue(value, options, preferred);
  return isSupportedCountry(country) ? getExampleNumber(country, mobileExamples)?.formatNational() : undefined;
}
